
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getSEOData, generateStructuredData } from '@/utils/seo';

interface MetaTagsProps {
  title?: string;
  description?: string;
  keywords?: string[];
  ogImage?: string;
  structuredData?: any;
  canonical?: string;
}

const MetaTags: React.FC<MetaTagsProps> = ({
  title,
  description,
  keywords,
  ogImage,
  structuredData,
  canonical
}) => {
  const location = useLocation();
  const seoData = getSEOData(location.pathname);
  
  const finalTitle = title || seoData.title;
  const finalDescription = description || seoData.description;
  const finalKeywords = keywords || seoData.keywords;
  const finalOGImage = ogImage || seoData.ogImage;
  const finalStructuredData = structuredData || seoData.structuredData;
  const finalCanonical = canonical || `https://juvmedspa.com${location.pathname}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords.join(', ')} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={finalCanonical} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      {finalOGImage && <meta property="og:image" content={finalOGImage} />}
      <meta property="og:site_name" content="JUV Medspa™" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={finalCanonical} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      {finalOGImage && <meta name="twitter:image" content={finalOGImage} />}
      
      {/* Additional Meta Tags */}
      <meta name="author" content="JUV Medspa™" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#A55C52" />
      
      {/* Structured Data */}
      {finalStructuredData && (
        <script type="application/ld+json">
          {generateStructuredData(finalStructuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default MetaTags;
